// Pure display helpers for the Marketing tab (round three #6). Every number
// is calculated on the server (src/shared/marketing.ts); these only format,
// scale and group what arrives, so they stay React-free and unit-testable.

import { addCalendarDays, calendarMonthName, melbourneClock, melbourneDate, previousMonthKey } from '../../shared/dates';
import { formatMoney } from './moneyFormat';
import {
  CEILING_TARGET_METRICS,
  MONEY_TARGET_METRICS,
  type AttentionItem,
  type MarketingChannel,
  type MarketingTotals,
  type MetaAdsErrorCode,
  type MetaAdsStatus,
  type MetaMetrics,
  type ReviewCadence,
  type StrategyStatus,
  type StrategySummary,
  type TargetMetric,
  type TargetProgress,
} from '../../shared/marketing';
import type { LeadStatus } from '../../shared/types';

/** "$4,800" under $10k, "$14.8k" above — card and table revenue. */
export function kMoney(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const dollars = cents / 100;
  if (Math.abs(dollars) < 10_000) return formatMoney(Math.round(dollars) * 100);
  return `$${(Math.round(dollars / 100) / 10).toLocaleString('en-AU')}k`;
}

/** ROI ratio → "+1,739%" / "-100%" / "—". */
export function formatRoi(roi: number | null | undefined): string {
  if (roi === null || roi === undefined || !Number.isFinite(roi)) return '—';
  const pct = Math.round(roi * 100);
  return `${pct > 0 ? '+' : ''}${pct.toLocaleString('en-AU')}%`;
}

/** "$18.39 back per $1" — revenue returned per dollar spent. */
export function backPerDollar(t: Pick<MarketingTotals, 'revenue_cents' | 'spend_cents'>): string | null {
  if (t.spend_cents <= 0) return null;
  const back = t.revenue_cents / t.spend_cents;
  return `$${back.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} back per $1`;
}

/** "2026-10" → "Oct" (or "October" with long). Sept stays "Sep" — the app's short month. */
export function monthLabel(key: string, long = false): string {
  return calendarMonthName(key, !long);
}

/** "1 job" / "2 jobs". */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export const isMoneyMetric = (m: TargetMetric) => MONEY_TARGET_METRICS.includes(m);
export const isCeilingMetric = (m: TargetMetric) => CEILING_TARGET_METRICS.includes(m);

/** A target or actual in its metric's unit: cents → "$35", counts → "6" / "1.5". */
export function formatTargetValue(metric: TargetMetric, value: number | null): string {
  if (value === null) return '—';
  return isMoneyMetric(metric) ? formatMoney(Math.round(value)) : String(Math.round(value * 10) / 10);
}

/** Modal input → stored target value. Money metrics are typed in dollars and
    stored in cents; counts must be whole numbers. Blank or invalid → null. */
export function parseTargetInput(metric: TargetMetric, raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (isMoneyMetric(metric)) return Math.round(n * 100);
  return Number.isInteger(n) ? n : null;
}

/** Hand-entered monthly count (posts, impressions…): whole numbers only.
    Blank → null; anything else invalid → undefined. */
export function parseWholeNumber(raw: string): number | null | undefined {
  const cleaned = raw.replace(/[,\s]/g, '');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isInteger(n) && n >= 0 ? n : undefined;
}

/** Stored target → modal input text (cents back to dollars). */
export function targetInputText(metric: TargetMetric, value: number): string {
  return isMoneyMetric(metric) ? String(value / 100) : String(value);
}

/** Goal-card hint under the bar. `spendCents` (the target period's spend)
    tells a CPL with no spend apart from one with spend but no leads. */
export function targetHint(p: TargetProgress, spendCents = 0): { text: string; behind: boolean } {
  const { metric, value } = p.target;
  if (p.actual === null) {
    if (metric === 'cpl_max') return { text: spendCents > 0 ? 'No leads yet' : 'No spend yet', behind: false };
    return { text: 'No data yet', behind: false };
  }
  if (isCeilingMetric(metric)) {
    return p.hit
      ? { text: 'Under ceiling', behind: false }
      : { text: `${formatMoney(Math.round(p.actual - value))} over ceiling`, behind: true };
  }
  const pct = value > 0 ? Math.round((p.actual / value) * 100) : 0;
  const state = p.hit ? 'target hit' : p.on_pace ? 'on pace' : 'behind pace';
  return { text: `${pct}% of target · ${state}`, behind: !p.hit && p.on_pace === false };
}

/** Bar heights (0–100) for one small-multiple row, scaled to its own max.
    A non-zero value never renders thinner than `minPct`, so a $12 month
    beside a $1,200 month is still visible. */
export function scaleSeries(values: readonly number[], minPct = 4): number[] {
  const max = Math.max(0, ...values);
  if (max <= 0) return values.map(() => 0);
  return values.map((v) => (v <= 0 ? 0 : Math.max(minPct, (v / max) * 100)));
}

/** Active strategies grouped per channel, in channel sort order; channel-less
    strategies (rare for active ones) trail under "No channel". */
export function groupByChannel(
  strategies: readonly StrategySummary[],
  channels: readonly MarketingChannel[],
): { channel: MarketingChannel | null; strategies: StrategySummary[] }[] {
  const groups = channels
    .map((channel) => ({ channel: channel as MarketingChannel | null, strategies: strategies.filter((s) => s.channel_id === channel.id) }))
    .filter((g) => g.strategies.length > 0);
  const orphans = strategies.filter((s) => !s.channel_id || !channels.some((c) => c.id === s.channel_id));
  if (orphans.length) groups.push({ channel: null, strategies: orphans });
  return groups;
}

/** Facebook-flavoured channels. Migration 0077 moved the live Facebook
    channel to data_source 'meta'; the name / lead-source checks keep any
    other Facebook-type channel showing the "Meta not connected" note. */
export function isMetaChannel(c: Pick<MarketingChannel, 'name' | 'default_lead_source' | 'data_source'>): boolean {
  return c.data_source === 'meta' || c.default_lead_source === 'facebook' || /facebook|meta/i.test(c.name);
}

// ---------- Meta Ads (Phase B) ----------

/** The Settings → Meta Ads card. `adSetId` deep-links one mapping row. */
export function metaSettingsHref(adSetId?: string): string {
  return `/settings?tab=general#${adSetId ? `meta-ad-set-${adSetId}` : 'meta-ads'}`;
}

/** Plain-English copy for each canonical sync/test error code. */
export const META_ERROR_TEXT: Record<MetaAdsErrorCode, string> = {
  not_configured: 'Not connected — add the ad account and access token',
  sync_in_progress: 'A Meta sync is already running — give it a moment',
  provider_unavailable: 'Meta didn’t respond — it’ll try again tonight',
  rate_limited: 'Meta’s rate limit was hit — try again in an hour',
  provider_access: 'Meta refused access — check the token can read this ad account',
  invalid_request: 'Meta rejected the request — check the ad account ID',
  invalid_payload: 'Meta sent numbers SS OS couldn’t read',
  content_too_large: 'Meta’s reply was too large to import',
};

export function metaErrorText(code: MetaAdsErrorCode | undefined | null): string {
  return (code && META_ERROR_TEXT[code]) || META_ERROR_TEXT.provider_unavailable;
}

/** 409s from POST /meta/sync: connection, active sync or 60-second guard. */
export function syncConflictText(message: string): string {
  if (/already running/i.test(message)) return META_ERROR_TEXT.sync_in_progress;
  return /not connected/i.test(message)
    ? 'Not connected — add the ad account and the access token first'
    : 'A sync started under a minute ago — give it a moment';
}

/** "12 Oct · 3:02am", Melbourne time. */
export function metaWhen(at: number): string {
  const iso = melbourneDate(at);
  return `${Number(iso.slice(8, 10))} ${calendarMonthName(iso.slice(0, 7), true)} · ${melbourneClock(at)}`;
}

/** "12,480". */
export function formatCount(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : n.toLocaleString('en-AU');
}

/** CTR ratio → "1.84%". */
export function formatCtr(ctr: number | null | undefined): string {
  return ctr === null || ctr === undefined || !Number.isFinite(ctr) ? '—' : `${(ctr * 100).toFixed(2)}%`;
}

/** One-line sync state for the Settings card header, the Channel page and the
    Marketing header. `tone` drives the dot / text colour. */
export function metaSyncState(status: MetaAdsStatus): { tone: 'ok' | 'bad' | 'off'; label: string; text: string } {
  const r = status.last_result;
  if (!status.configured) return { tone: 'off', label: 'Not connected', text: 'Meta not connected' };
  if (r && !r.ok) {
    const shown = status.last_sync_at ? ` · showing ${metaWhen(status.last_sync_at).split(' · ')[0]}` : '';
    return { tone: 'bad', label: 'Last sync failed', text: `Last sync failed${shown}` };
  }
  if (status.last_sync_at) return { tone: 'ok', label: 'Synced', text: `Synced ${metaWhen(status.last_sync_at)}` };
  return { tone: 'off', label: 'Not synced yet', text: 'Connected · not synced yet' };
}

/** Sum of per-strategy Meta numbers (the Channel total row and vitals);
    null when none of them has an ad set. CTR is recomputed from the sums. */
export function sumMeta(list: readonly (MetaMetrics | null | undefined)[]): MetaMetrics | null {
  const rows = list.filter((m): m is MetaMetrics => !!m);
  if (!rows.length) return null;
  const t = { spend_cents: 0, impressions: 0, reach: 0, clicks: 0, meta_leads: 0 };
  for (const m of rows) for (const k of Object.keys(t) as (keyof typeof t)[]) t[k] += m[k];
  return { ...t, ctr: t.impressions > 0 ? t.clicks / t.impressions : null };
}

/** Needs-attention rows: Meta items get friendly copy and a Settings link;
    every other kind passes through unchanged. */
export function attentionDisplay(a: AttentionItem, status: MetaAdsStatus | null | undefined): AttentionItem {
  if (a.kind === 'meta_sync_failed') {
    const r = status?.last_result;
    const failed = r && !r.ok;
    return {
      ...a,
      tag: failed ? 'Sync failed' : 'Sync stale',
      title: 'Meta Ads',
      detail: failed
        ? `${metaErrorText(r.code)}${status?.last_sync_at ? ` · numbers are from ${metaWhen(status.last_sync_at).split(' · ')[0]}` : ''}`
        : 'No successful sync in over 36 hours',
      href: metaSettingsHref(),
    };
  }
  if (a.kind === 'meta_unmapped') {
    return { ...a, tag: 'Unmapped', detail: 'Map it to a strategy in Settings → Meta Ads', href: metaSettingsHref(a.ad_set_id) };
  }
  return a;
}

/** "02/09/26" — rail dates in the mockup. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}`;
}

/** "starts 03/11" — backlog row sub-line. */
export function dayMonthSlash(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

/** "$15/day" · "$450/month" · "$2,000 total". */
export function budgetLabel(cents: number | null, period: string | null): string | null {
  if (cents === null) return null;
  const amount = formatMoney(cents);
  if (period === 'day') return `${amount}/day`;
  if (period === 'month') return `${amount}/mo`;
  if (period === 'total') return `${amount} total`;
  return amount;
}

/** "Copy of …" candidates: same channel, never ideas, never itself. */
export function copyCandidates<T extends { id: string; status: StrategyStatus; channel_id: string | null }>(
  list: readonly T[],
  channelId: string | null,
  exclude?: string,
): T[] {
  return list.filter((s) => s.channel_id === channelId && s.status !== 'idea' && s.id !== exclude);
}

export const LEAD_STAGE_TONE: Partial<Record<LeadStatus, 'accent' | 'gold' | 'ok' | 'danger'>> = {
  enquiry: 'accent',
  discovery_call: 'accent',
  proposal_creation: 'gold',
  proposal_sent: 'gold',
  negotiation: 'gold',
  locked_in: 'ok',
  won: 'ok',
  lost: 'danger',
};

/** First review date for a new strategy: one cadence after it starts. */
export function nextReviewDate(from: string, cadence: ReviewCadence): string {
  if (cadence === '1w') return addCalendarDays(from, 7);
  if (cadence === '2w') return addCalendarDays(from, 14);
  const [y, m, d] = from.split('-').map(Number);
  const months = cadence === '1q' ? 3 : 1;
  const total = y * 12 + (m - 1) + months;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}

/** The month "+ <Month> numbers" opens: last month while it is still missing
    (and the strategy was running then), otherwise this month. */
export function metricsMonth(today: string, startDate: string | null, manual: Record<string, Record<string, number>>): string {
  const current = today.slice(0, 7);
  const prev = previousMonthKey(current);
  const ranLastMonth = !startDate || startDate.slice(0, 7) <= prev;
  return ranLastMonth && !manual[prev] ? prev : current;
}
